import React, { useState, useEffect } from 'react';
import { Shield, Settings, Key, Database } from 'lucide-react';
import ApplicationList from '../../components/rbac/ApplicationList';
import RoleList from '../../components/rbac/RoleList';
import PermissionList from '../../components/rbac/PermissionList';

const RBACPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('applications');

  return (
    <div className="container mx-auto py-8 space-y-8 px-4 md:px-6">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-blue-900/80 via-indigo-900/80 to-purple-900/80 rounded-xl p-6 shadow-lg border border-blue-700/30 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(99,102,241,0.1)_1px,transparent_1px)] bg-[length:24px_24px] opacity-20"></div>
        
        {/* Accent line at top */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400/70 via-indigo-300/70 to-purple-400/70"></div>
        
        <div className="flex items-center space-x-4 relative z-10">
          <div className="bg-blue-500/20 p-3 rounded-lg border border-blue-500/30">
            <Shield className="h-8 w-8 text-blue-300" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">
              Access Control
            </h1>
            <p className="text-blue-200/70 mt-1">
              Manage applications, roles, and permissions across the platform.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-background-card rounded-xl shadow-lg border border-border-dark overflow-hidden">
        {/* Modern tab navigation */}
        <div className="bg-background-card/80 backdrop-blur-sm border-b border-border-dark">
          <div className="flex overflow-x-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
            <button 
              className={`px-6 py-4 font-medium transition-all duration-200 flex items-center space-x-2 relative
                ${activeTab === 'applications' 
                  ? 'text-blue-400 font-semibold' 
                  : 'text-gray-400 hover:text-gray-200'}`}
              onClick={() => setActiveTab('applications')}
            >
              <Database className="h-4 w-4" />
              <span>Applications</span>
              {activeTab === 'applications' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
              )}
            </button>
            <button 
              className={`px-6 py-4 font-medium transition-all duration-200 flex items-center space-x-2 relative
                ${activeTab === 'roles' 
                  ? 'text-blue-400 font-semibold' 
                  : 'text-gray-400 hover:text-gray-200'}`}
              onClick={() => setActiveTab('roles')}
            >
              <Settings className="h-4 w-4" />
              <span>Roles</span>
              {activeTab === 'roles' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
              )}
            </button>
            <button 
              className={`px-6 py-4 font-medium transition-all duration-200 flex items-center space-x-2 relative
                ${activeTab === 'permissions' 
                  ? 'text-blue-400 font-semibold' 
                  : 'text-gray-400 hover:text-gray-200'}`}
              onClick={() => setActiveTab('permissions')}
            >
              <Key className="h-4 w-4" />
              <span>Permissions</span>
              {activeTab === 'permissions' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
              )}
            </button>
          </div>
        </div>

        {/* Content area with subtle animation */}
        <div className="p-6 animate-fadeIn">
          {activeTab === 'applications' && (
            <div className="bg-gradient-to-b from-background-card/80 to-background p-0.5 rounded-lg">
              <div className="bg-background-card rounded-lg p-6">
                <ApplicationList />
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="bg-gradient-to-b from-background-card/80 to-background p-0.5 rounded-lg">
              <div className="bg-background-card rounded-lg p-6">
                <RoleList />
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="bg-gradient-to-b from-background-card/80 to-background p-0.5 rounded-lg">
              <div className="bg-background-card rounded-lg p-6">
                <PermissionList />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RBACPage; 